// The desktop's "Working together": one rule for her room (screens/desktop/Room.tsx), the small window (K2, PiP) and the
// menu bar timer (store.ts setTray). The card being worked on is the card still to do with the latest startedAt (since
// 11-05 the server keeps one session at a time and the room's ✕ ends it there). Builds before 11-05 stopped a session
// in the room only, keeping its id in localStorage (both windows share it; 'storage' events tell the other window):
// since 11-05 the room forgets that list once (clearStopped) and `stop` is no longer called by the app.
import type { AppState, Card } from '@shared/types';
import type { DeskTime } from '../screens/desktop/media';
import { localMinutes } from '@shared/rules';

export const STOPPED_KEY = 'xm.room.stopped';
/** Fired in this window when the list changes here (other windows get the 'storage' event): the menu bar follows. */
export const STOPPED_EVENT = 'xm:stopped';
const changed = () => { try { window.dispatchEvent(new Event(STOPPED_EVENT)); } catch { /* no window */ } };

/** Session ids stopped in the room. Older builds kept them in sessionStorage (this window only): read both. */
export function readStopped(): string[] {
  const out: string[] = [];
  for (const get of [() => localStorage.getItem(STOPPED_KEY), () => sessionStorage.getItem(STOPPED_KEY)]) {
    try {
      const v = JSON.parse(get() || '[]');
      if (Array.isArray(v)) for (const x of v) if (typeof x === 'string' && !out.includes(x)) out.push(x);
    } catch { /* storage blocked or not JSON */ }
  }
  return out;
}

/** Stop a session in the room (the ✕ on the timer card). Keeps the last 40 ids. */
export function stop(id: string) {
  const all = readStopped().filter((x) => x !== id).concat([id]).slice(-40);
  try { localStorage.setItem(STOPPED_KEY, JSON.stringify(all)); } catch { /* storage blocked */ }
  changed();
}

/** Forget a stopped session (it is started again). */
export function unstop(id: string) {
  const left = readStopped().filter((x) => x !== id);
  try { localStorage.setItem(STOPPED_KEY, JSON.stringify(left)); } catch { /* storage blocked */ }
  try { if (sessionStorage.getItem(STOPPED_KEY) != null) sessionStorage.setItem(STOPPED_KEY, JSON.stringify(left)); } catch { /* ignore */ }
  changed();
}

/** Forget every stopped session (11-05: the room's ✕ ends the session on the server, so the list is only left over). */
export function clearStopped() {
  try { localStorage.removeItem(STOPPED_KEY); } catch { /* storage blocked */ }
  try { sessionStorage.removeItem(STOPPED_KEY); } catch { /* storage blocked */ }
  changed();
}

export function deskSession(st: AppState, stopped: string[] = readStopped()): Card | null {
  return st.plan.cards
    .filter((c) => c.st === 'todo' && !!c.startedAt && !stopped.includes(c.id))
    .sort((a, b) => Date.parse(b.startedAt!) - Date.parse(a.startedAt!))[0] || null;
}

/** Day or night for her pictures, from the user's own clock: night from 6 PM to 6 AM (the room's rule too). */
export function deskTimeAt(ms: number, tz: string): DeskTime {
  const m = localMinutes(ms, tz);
  return m >= 18 * 60 || m < 6 * 60 ? 'night' : 'day';
}
